import { ENDPOINTS } from 'config/endpoints';
import { useMutation } from '@tanstack/react-query';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import CronoError from 'crono-fe-common/types/error';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { getLabelForIntegrationType } from 'crono-fe-common/utils/crm-utils';

export type ZohoOauthCode = {
  code: string;
  // A grant code can only be exchanged at the data center that issued it, so
  // this is forwarded exactly as Zoho put it on the redirect.
  accountsServer: string | null;
};

const integrationLabel = getLabelForIntegrationType(IntegrationType.ZOHO);

const ZOHO_CONNECT_ERROR = 'Error while connecting with the CRM';

// Same reasoning as the Attio exchange: the global mutation cache emits the
// success toast before the mutation's own callback, so an explicit
// `isSuccess: false` has to fail the mutation here.
const zohoAuthCallback = async (data: ZohoOauthCode) => {
  const request: Request = {
    url: ENDPOINTS.zoho.authCallback,
    config: {
      method: 'post',
      data: data,
    },
  };

  const response: Response<null> = await client(request);

  if (response.data.isSuccess === false) {
    const error: CronoError = {
      message: ZOHO_CONNECT_ERROR,
      response: {
        status: response.status,
        data: { errors: response.data.errors ?? [] },
      },
    };
    throw error;
  }

  return response;
};

const useZohoAuthCallback = () =>
  useMutation<Response<null>, CronoError, ZohoOauthCode>(zohoAuthCallback, {
    meta: {
      successMessage: `Successfully logged into ${integrationLabel}`,
      errorMessage: ZOHO_CONNECT_ERROR,
    },
  });

export default useZohoAuthCallback;
